// src/components/Mannequin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF, useTexture } from "@react-three/drei";
import * as THREE from "three";
import GarmentMesh from "./GarmentMesh";
import { useDepthOcclusion } from "./DepthOcclusionProvider";
import { poseSpecFromLandmarks, buildHandsAtSidesPoseSpec } from "../ml/poseToRigify";
import { estimateHumanLandmarks } from "../ml/estimateHumanLandmarks";
import { cacheRestPose, restoreRestPose, applyPoseToSkeleton } from "../rig/skeletonPose";
import { computeGarmentFit } from "../rig/garmentFit";
import { garmentKeyFromUrl } from "../rig/garmentMetrics";
import Wig from "./Wig";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { bakeProjectedUvAtlas } from "../ml/bakeProjectedUvAtlas";

/* =====================================================
   MANNEQUIN CALIBRATION (LOCKED – v1)
===================================================== */
const TORSO_WIDTH = 0.95;
const HIP_WIDTH = 0.85;

 const ASSET_BASE =
  process.env.REACT_APP_ASSET_BASE_URL ||
  "https://pub-f0f38b8dd0224e2f89367893cf6bb0d7.r2.dev";

const MANNEQUIN_GLB_URL = `${ASSET_BASE}/models/mannequin.glb`;


export const BODY_REFS = {
  shoulderX: 0.323299, // <-- your mannequin shoulder X
  hipX: 0.287169       // <-- your mannequin hip X (replace with your final)
};


// If mannequin appears not to rotate but garments do, mannequin GLB likely has a baked 180° yaw.
// This offset cancels it so viewYaw affects mannequin + garments consistently.
const MANNEQUIN_MODEL_YAW_FIX = 0; // if wrong direction, change to -Math.PI
const DEBUG_SHOW_SKELETON = true;

function isValidDataImage(s) {
  if (!s || typeof s !== "string") return false;
  if (!s.startsWith("data:image/")) return true; // normal URL is fine
  // data URL must have payload after comma
  const i = s.indexOf(",");
  return i >= 0 && s.length > i + 1;
}

/* =====================================================
   HELPERS
===================================================== */
function GarmentMeshWithTexture({
  item,
  avatarParams,
  poseSpec,
  fitData,
  onBounds,
  overlapMode,
  clipY,
  clipTarget,
  occluderFrontSrc = null,
  occluderBackSrc = null,
  occluderMin = null,
  occluderMax = null,
  occluderCategory = null,
  occluderFitData = null,
  stencilMode = "none",
  disableClip = false,
}) {
const [fillColor] = useState("#b8b8b8");
const [uvAtlasSrc, setUvAtlasSrc] = useState(null);
const [uvAtlasStatus, setUvAtlasStatus] = useState("idle");
const [uvAtlasRange, setUvAtlasRange] = useState(null);

  const EMPTY_PNG =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=";

  // Prefer extracted cutouts produced earlier (BrowsePanel "Add")
const frontCandidate =
  item?.extractedUrl ||
  item?.try_on?.assets?.front ||
  item?.front ||
  item?.image ||
  null;

const backCandidate =
  item?.extractedBackUrl ||
  item?.try_on?.assets?.back ||
  item?.back ||
  item?.imageBack ||
  item?.extractedUrl ||
  item?.try_on?.assets?.front ||
  item?.front ||
  item?.image ||
  null;

const useSimpleUvAtlasPrototype =
  String(item?.meshUrl || "").includes("pant_relaxed.glb") &&
  !!frontCandidate;

useEffect(() => {
  let cancelled = false;

  async function run() {
    if (!useSimpleUvAtlasPrototype || !frontCandidate) {
      setUvAtlasSrc(null);
setUvAtlasRange(null);
setUvAtlasStatus("idle");
return;
    }

    try {
      setUvAtlasStatus("building");

  const result = await bakeProjectedUvAtlas({
  imageUrl: frontCandidate,
  glbUrl: item?.meshUrl,
  size: 1024,
  debug: false,
});

      if (cancelled) return;

      setUvAtlasSrc(result.dataUrl);
setUvAtlasRange(result.uvRange || null);
setUvAtlasStatus("ready");

console.log("[UV BAKE] projected atlas created", {
  itemId: item?.id,
  meshUrl: item?.meshUrl,
  meshName: result.meshName,
  baseColor: result.baseColor,
  sampleCount: result.sampleCount,
  paintedPixels: result.paintedPixels,
  skippedTransparent: result.skippedTransparent,
  vertexCount: result.vertexCount,
  uvCount: result.uvCount,
  triCount: result.triCount,
  uvRange: result.uvRange,
});

    } catch (err) {
      if (cancelled) return;

      console.warn("[UV BAKE] failed, falling back to projection mode", err);

     setUvAtlasSrc(null);
setUvAtlasRange(null);
setUvAtlasStatus("failed");
    }
  }

  run();

  return () => {
    cancelled = true;
  };
}, [useSimpleUvAtlasPrototype, frontCandidate, item?.id, item?.meshUrl]);

// Never pass null into useTexture.
// If a UV atlas exists, use it as both front and back texture.
const frontSrc = uvAtlasSrc || frontCandidate || EMPTY_PNG;
const backSrc = uvAtlasSrc || backCandidate || EMPTY_PNG;

// ✅ Hooks stay unconditional
const frontTex = useTexture(frontSrc);
const backTex = useTexture(backSrc);

useEffect(() => {
  if (frontTex) frontTex.needsUpdate = true;
  if (backTex) backTex.needsUpdate = true;
}, [frontTex, backTex]);

  // Loading means: we expected extracted but don't have it yet.
  // If you always add extractedUrl before pushing into fitting room, this will be false.
  const isLoading = !!item?._extracting;
  
  console.log("[GarmentMeshWithTexture] texture sources", {
  itemId: item?.id,
  category: item?.category,
  frontCandidate,
  backCandidate,
  frontSrc,
  backSrc,
});

console.log("[TEXTURE DEBUG]", {
  frontSrc,
  backSrc,
  frontTexImage: frontTex?.image,
  backTexImage: backTex?.image,
});
    return (
    <GarmentMesh
  key={`${item?.id || item?.name || item?.meshUrl}|${String(frontSrc).slice(0, 30)}|${String(backSrc).slice(0, 30)}`}
  glbPath={item?.meshUrl}
  frontTexture={frontTex}
  backTexture={backTex}
  fillColor={fillColor}
  garmentType={item?.category}
  avatarParams={avatarParams}
  poseSpec={poseSpec}
  projectionFit={fitData || null}
  renderOrder={11}
  isLoading={isLoading}
  onBounds={onBounds}
  overlapMode={overlapMode}
  clipY={clipY}
  clipTarget={clipTarget}
  occluderFrontSrc={occluderFrontSrc}
  occluderBackSrc={occluderBackSrc}
  occluderMin={occluderMin}
  occluderMax={occluderMax}
  occluderCategory={occluderCategory}
  occluderFitData={occluderFitData}
  stencilMode={stencilMode}
  disableClip={disableClip}
  debugMeshUV={false}
  useMeshUVMapping={!!uvAtlasSrc && uvAtlasStatus === "ready"}
  uvAtlasRange={uvAtlasRange}
/>
  );
}

function categoryWidthRef(category) {
  return category === "bottom" ? HIP_WIDTH : TORSO_WIDTH;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
function normalizeImgUrl(url) {
  if (!url) return null;

  const s = String(url);

  // Relative → make absolute at current origin
  if (s.startsWith("/")) return `${window.location.origin}${s}`;

  // Absolute → if host differs, rewrite to current origin but keep path/query
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const u = new URL(s);

      // If it's a local dev host mismatch (localhost vs 127.0.0.1), force same-origin
      const here = window.location.origin;
      const sameHost = u.origin === here;

      if (!sameHost && (u.hostname === "localhost" || u.hostname === "127.0.0.1")) {
        return `${here}${u.pathname}${u.search}${u.hash}`;
      }

      return s;
    } catch {
      return s;
    }
  }

  return s;
}


/* =====================================================
   SHAPE KEYS (0..1)
===================================================== */

const SHAPE_KEYS = ["Arm_up", "Bust_up", "Waist_up", "Hip_up", "Leg_up"];

function clamp01(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

function getShapeKeyValues(avatarParams) {
  return {
    Arm_up: clamp01(avatarParams?.Arm_up ?? 0),
    Bust_up: clamp01(avatarParams?.Bust_up ?? 0),
    Waist_up: clamp01(avatarParams?.Waist_up ?? 0),
    Hip_up: clamp01(avatarParams?.Hip_up ?? 0),
    Leg_up: clamp01(avatarParams?.Leg_up ?? 0),
  };
}

function applyShapeKeys(root, values) {
  root.traverse((obj) => {
    if (!obj.isMesh) return;
    const dict = obj.morphTargetDictionary;
    const inf = obj.morphTargetInfluences;
    if (!dict || !inf) return;

    for (const key of SHAPE_KEYS) {
      const idx = dict[key];
      if (idx === undefined) continue;
      inf[idx] = values[key] ?? 0;
    }
  });
}
function applySkinColor(root, skin) {
  if (!skin?.base) return;

  const color = new THREE.Color(skin.base);

  root.traverse((obj) => {
    if (!obj.isMesh) return;
    if (!obj.material) return;

    if (Array.isArray(obj.material)) {
      obj.material.forEach((mat) => {
        if (mat.color) {
          mat.color.copy(color);
          mat.needsUpdate = true;
        }
      });
    } else {
      if (obj.material.color) {
        obj.material.color.copy(color);
        obj.material.needsUpdate = true;
      }
    }
  });
}
function forceBindPose(root) {
  if (!root) return;
  root.traverse((o) => {
    if (o?.isSkinnedMesh && o?.skeleton) {
      o.skeleton.pose();          // reset bones to bind pose
      o.skeleton.update();        // ensure bone matrices refresh
    }
  });
  root.updateMatrixWorld(true);
}

function normalizePoseCategory(category) {
  const s = String(category || "").toLowerCase().split(":")[0];

  if (s === "pants" || s === "jeans" || s === "trousers" || s === "skirt" || s === "shorts") {
    return "bottom";
  }

  if (s === "jacket" || s === "coat" || s === "blazer" || s === "cardigan") {
    return "outer";
  }

  return s;
}

  
/* =====================================================
   MAIN MANNEQUIN
===================================================== */

export default function Mannequin({
  avatarParams,
  selectedItems,
  garmentMetricsMap = null,
  bodyRefs = BODY_REFS,
  poseMode = "catalog",
  forceFixedTryOnPose = true,
}) {

 
const { scene } = useGLTF(MANNEQUIN_GLB_URL);
  const groupRef = useRef(null);
  const mannequinRef = useRef(null); // <primitive> ref
  const skeletonHelperRef = useRef(null);
  const [poseSpec, setPoseSpec] = useState(null); // will store { src: string, bones: [...] } or null
  const [isTucked, setIsTucked] = useState(false);
  const [filteredPoseSpec, setFilteredPoseSpec] = useState(null);
  const poseReqRef = useRef(0);

  // --- bounds plumbing (needed for computeGarmentFit) ---
  const [mannequinBounds, setMannequinBounds] = useState(null);
  const garmentBoundsRef = useRef(new Map()); // key -> bounds object
  const [boundsTick, setBoundsTick] = useState(0);

  const visibleScene = useMemo(() => cloneSkeleton(scene), [scene]);

    const fixedTryOnPoseSpec = useMemo(() => {
    return buildHandsAtSidesPoseSpec();
  }, []);

  useEffect(() => {
  if (!visibleScene) return;
  cacheRestPose(visibleScene);
}, [visibleScene]);


  const depth = useDepthOcclusion();

  // Register mannequin root for depth occlusion system
  useEffect(() => {
    if (!depth || !groupRef.current) return;
    depth.registerMannequinRoot(groupRef.current);
    depth.setMannequinReady(true);
    return () => depth.setMannequinReady(false);
  }, [depth]);

  useEffect(() => {
  if (!visibleScene) return;

  const names = [];
  visibleScene.traverse((o) => {
    if (o?.isBone) names.push(o.name);
  });

}, [visibleScene]);

  // Apply morph targets from avatarParams
  useEffect(() => {
    if (!visibleScene) return;
    applyShapeKeys(visibleScene, getShapeKeyValues(avatarParams));
  }, [visibleScene, avatarParams]);

  // Apply skin color from avatarParams
useEffect(() => {
  if (!visibleScene) return;
  applySkinColor(visibleScene, avatarParams?.skin);
}, [visibleScene, avatarParams?.skin?.base]);

const activeItem = useMemo(() => {
  return (
    selectedItems?.dress ||
    selectedItems?.top ||
    selectedItems?.bottom ||
    selectedItems?.outer ||
    null
  );
}, [selectedItems]);

const activePoseImageUrl = useMemo(() => {
  if (forceFixedTryOnPose) return null;
  if (!activeItem) return null;

  const isBack =
    poseMode === "back" ||
    poseMode === "rear" ||
    poseMode === "backView";

  const rawUrl =
    (isBack
      ? (activeItem?.imageBack || activeItem?.imageRear || activeItem?.backImage)
      : (activeItem?.image || activeItem?.imageFront || activeItem?.photoUrl)
    ) ||
    activeItem?.image ||
    activeItem?.imageFront ||
    activeItem?.photoUrl ||
    null;

  return normalizeImgUrl(rawUrl);
}, [activeItem, poseMode, forceFixedTryOnPose]);

// Compute mannequin bounds helper
function computeObjectBounds(obj3d) {
  if (!obj3d) return null;
  const box = new THREE.Box3().setFromObject(obj3d);
  if (!box || !isFinite(box.min.x) || !isFinite(box.max.x)) return null;

  const size = new THREE.Vector3();
  box.getSize(size);

  return {
    min: box.min.clone(),
    max: box.max.clone(),
    size,
  };
}

// Pose Estimation (tag pose with source URL + ignore stale async results)
useEffect(() => {
  poseReqRef.current += 1;
  const reqId = poseReqRef.current;

  setPoseSpec(null);
  if (visibleScene) restoreRestPose(visibleScene);

  if (forceFixedTryOnPose) {
    const fixedBones = fixedTryOnPoseSpec?.bones?.length ? fixedTryOnPoseSpec.bones : null;

    if (!fixedBones) {
      setPoseSpec(null);
      return;
    }

    setPoseSpec({
      src: "__fixed_tryon_pose__",
      bones: fixedBones,
      meta: fixedTryOnPoseSpec.meta || {},
    });

    console.log("[POSE_SPEC_CREATED]", {
      src: "__fixed_tryon_pose__",
      boneCount: fixedBones.length,
      bones: fixedBones.map((b) => b.name),
      spec: {
        src: "__fixed_tryon_pose__",
        bones: fixedBones,
        meta: fixedTryOnPoseSpec.meta || {},
      },
    });

    return;
  }

  async function runPoseEstimation() {
    if (!activePoseImageUrl) return;

    try {
      const res = await estimateHumanLandmarks({ imageUrl: activePoseImageUrl });

      if (poseReqRef.current !== reqId) return;

      if (!res?.poseLandmarks || res?.confidence < 0.2) {
        setPoseSpec(null);
        if (visibleScene) restoreRestPose(visibleScene);
        return;
      }

      const spec = poseSpecFromLandmarks(res.poseLandmarks);
      const bones = spec?.bones?.length ? spec.bones : null;

      if (!bones) {
        setPoseSpec(null);
        if (visibleScene) restoreRestPose(visibleScene);
        return;
      }

      console.log("[POSE_SPEC_CREATED]", {
        src: activePoseImageUrl,
        boneCount: bones.length,
        bones: bones.map((b) => b.name),
        spec: { src: activePoseImageUrl, bones, meta: spec.meta || {} },
      });

      setPoseSpec({ src: activePoseImageUrl, bones, meta: spec.meta || {} });
    } catch (err) {
      if (poseReqRef.current !== reqId) return;
      console.warn("[Pose] landmark estimation failed", err, "imgUrl =", activePoseImageUrl);
      setPoseSpec(null);
      if (visibleScene) restoreRestPose(visibleScene);
    }
  }

  runPoseEstimation();
}, [activePoseImageUrl, visibleScene, forceFixedTryOnPose, fixedTryOnPoseSpec]);


// --- Apply Pose
useEffect(() => {
  if (!visibleScene) return;
  setFilteredPoseSpec(null);

  const category =
    selectedItems?.dress?.category ||
    selectedItems?.outer?.category ||
    selectedItems?.top?.category ||
    selectedItems?.bottom?.category ||
    null;

const cat = normalizePoseCategory(category);

const moveArms = cat === "top" || cat === "outer" || cat === "dress";
const moveLegs = cat === "bottom";

// Always reset to *cached rest* first (do NOT mix bind pose + rest pose)
restoreRestPose(visibleScene);
// restoreRestPose(visibleScene); // optional, leave commented for now

  // Only apply pose if it was computed for the CURRENT active image
if (!poseSpec?.bones?.length) return;

const isFixedPose = poseSpec?.src === "__fixed_tryon_pose__";
if (!isFixedPose && poseSpec.src !== activePoseImageUrl) return;

  const filtered = {
    bones: (poseSpec.bones || []).filter((b) => {
      const n = String(b?.name || "").toLowerCase();
const isArm =
  n.includes("upper_arm") ||
  n.includes("upperarm") ||
  n.includes("forearm") ||
  n.includes("hand") ||
  n.includes("shoulder");

const isLeg =
  n.includes("thigh") ||
  n.includes("shin") ||
  n.includes("foot") ||
  n.includes("toe") ||
  n.includes("upperleg") ||
  n.includes("lowerleg");

const isTorso =
  n === "hips" ||
  n === "chest" ||
  n.includes("spine") ||
  n === "neck" ||
  n === "head";

// Tops/dresses/outers: arms + torso + hips (hips helps twist alignment)
if (moveArms && (isArm || isTorso)) return true;

// Bottoms: legs + hips (hips is essential for side-step / pelvis lead)
if (moveLegs && (isLeg || n === "hips")) return true;

return false;
    }),
  };

  console.log("[POSE_FILTER]", {
  rawCategory: category,
  normalizedCategory: cat,
  inputBones: poseSpec?.bones?.map((b) => b.name),
  keptBones: filtered.bones.map((b) => b.name),
});

  if (!filtered.bones.length) return;
  
  setFilteredPoseSpec(filtered);

  // Apply WITHOUT reset, because we already reset above.
  applyPoseToSkeleton(visibleScene, filtered, { reset: false });
}, [visibleScene, poseSpec, selectedItems, activePoseImageUrl]);

// --- Measure mannequin bounds (used as the "height contract" reference) ---
useEffect(() => {
  if (!mannequinRef.current) return;

  // Find a mesh inside the mannequin scene to measure in LOCAL geometry space
  let bodyMesh = null;
  mannequinRef.current.traverse?.((o) => {
    if (!bodyMesh && o?.isSkinnedMesh && o?.geometry) bodyMesh = o;
  });
  if (!bodyMesh?.geometry) return;

  const geom = bodyMesh.geometry;
  if (!geom.boundingBox) geom.computeBoundingBox();
  const bb = geom.boundingBox;
  if (!bb) return;

  const min = bb.min.clone();
  const max = bb.max.clone();
  const size = new THREE.Vector3(max.x - min.x, max.y - min.y, max.z - min.z);

  setMannequinBounds({ min, max, size });
}, [
  visibleScene,
  avatarParams?.Arm_up,
  avatarParams?.Bust_up,
  avatarParams?.Waist_up,
  avatarParams?.Hip_up,
  avatarParams?.Leg_up,
  poseSpec,
]);

  const overlays = useMemo(() => {
    const list = [];
    if (selectedItems?.dress) list.push(selectedItems.dress);
    else {
      if (selectedItems?.top) list.push(selectedItems.top);
      if (selectedItems?.bottom) list.push(selectedItems.bottom);
    }
    if (selectedItems?.outer) list.push(selectedItems.outer);
    return list.filter(Boolean);
  }, [selectedItems]);

  const hasTop = !!selectedItems?.top;
const hasBottom = !!selectedItems?.bottom;
const hasOuter = !!selectedItems?.outer;
const topVariant = selectedItems?.top?.variant || null;
const isFittedTop = topVariant === "fitted";

let overlapMode = null;

if (hasOuter && hasBottom) {
  overlapMode = "outer";
} else if (hasTop && hasBottom) {
  if (isFittedTop) {
    overlapMode = "tucked"; // 🔥 force fitted tops to tuck
  } else {
    overlapMode = isTucked ? "tucked" : "untucked";
  }
}

let clipY = null;

if (overlapMode && mannequinBounds) {
  // fallback
  clipY = mannequinBounds.min.y + mannequinBounds.size.y * 0.55;

  if (overlapMode === "untucked" && hasBottom) {
    const bottomKey = selectedItems.bottom?.meshUrl;
    const bottomBounds = garmentBoundsRef.current.get(bottomKey);

    let waistbandY = clipY;
    if (bottomBounds?.max?.y !== undefined && bottomBounds?.size?.y) {
      waistbandY = bottomBounds.max.y - bottomBounds.size.y * 0.10;
    }

    // For fitted/relaxed tops, use the top hem too.
    // We take the LOWER line so more of the pants gets hidden under the shirt.
    let topHemY = waistbandY;
    if (hasTop) {
      const topKey = selectedItems.top?.meshUrl;
      const topBounds = garmentBoundsRef.current.get(topKey);

      if (topBounds?.min?.y !== undefined && topBounds?.size?.y) {
        topHemY = topBounds.min.y + topBounds.size.y * 0.08;
      }
    }

    clipY = Math.max(waistbandY, topHemY);
  }

  if (overlapMode === "tucked" && hasBottom) {
    const bottomKey = selectedItems.bottom?.meshUrl;
    const bottomBounds = garmentBoundsRef.current.get(bottomKey);

    if (bottomBounds?.max?.y !== undefined && bottomBounds?.size?.y) {
      clipY = bottomBounds.max.y - bottomBounds.size.y * 0.10;
    }
  }

  if (overlapMode === "outer") {
    const outerKey = selectedItems.outer?.meshUrl;
    const outerBounds = garmentBoundsRef.current.get(outerKey);

    if (outerBounds?.min?.y !== undefined && outerBounds?.size?.y) {
      clipY = outerBounds.min.y + outerBounds.size.y * 0.12;
    }
  }
}

    // ✅ changing avatarParams should force garment remount so projection rebuilds
  const shapeSig = useMemo(() => {
  const v = getShapeKeyValues(avatarParams);
  return `${v.Arm_up.toFixed(3)}-${v.Bust_up.toFixed(3)}-${v.Waist_up.toFixed(3)}-${v.Hip_up.toFixed(3)}-${v.Leg_up.toFixed(3)}`;
}, [
  avatarParams?.Arm_up,
  avatarParams?.Bust_up,
  avatarParams?.Waist_up,
  avatarParams?.Hip_up,
  avatarParams?.Leg_up,
]);

useEffect(() => {
  if (!visibleScene) return;

  const hasAny =
    !!(selectedItems?.dress || selectedItems?.top || selectedItems?.bottom || selectedItems?.outer);

  if (!hasAny) {
    restoreRestPose(visibleScene);
    setPoseSpec(null);
    setFilteredPoseSpec(null);
  }
}, [visibleScene, selectedItems]);

  return (
    <>
      <group>
        {/* INNER GROUP */}
        <group ref={groupRef}>
          <group rotation={[0, 0, 0]}>
            <primitive ref={mannequinRef} object={visibleScene} />

            <Wig
              key={`${avatarParams?.hair?.style ?? "none"}_${avatarParams?.hair?.color ?? "none"}`}
              hair={avatarParams?.hair}
            />
          </group>

          {overlays.map((item) => {
            if (!item?.meshUrl) return null;

            const boundsKey = String(item?.meshUrl || "");
            const metricsKey = garmentKeyFromUrl(item?.meshUrl || "");
            const garmentMetrics = garmentMetricsMap?.[metricsKey] || null;

            const gb = garmentBoundsRef.current.get(boundsKey);

            let fitData = (gb && mannequinBounds)
              ? computeGarmentFit({
                  category: item?.category,
                  variant: item?.variant,
                  garmentBounds: gb,
                  mannequinBounds,
                  garmentMetrics,
                  bodyRefs,
                })
              : null;

            if (fitData) {
              const baseCat = String(item?.category || "").split(":")[0];

              if (baseCat === "outer") {
                const boxH = gb?.size?.y || 1;

                fitData = {
                  ...fitData,
                  scaleX: THREE.MathUtils.clamp(fitData.scaleX, 0.85, 1.10),
                  scaleY: THREE.MathUtils.clamp(fitData.scaleY, 0.85, 1.10),
                  scaleZ: THREE.MathUtils.clamp(fitData.scaleZ ?? fitData.scaleX, 0.94, 1.10),
                  yOffset: THREE.MathUtils.clamp(fitData.yOffset, -0.18 * boxH, 0.06 * boxH),
                };
              }
            }

            if (fitData) {
              console.log("[FIT]", item?.category, {
                scaleX: fitData.scaleX,
                scaleY: fitData.scaleY,
                scaleZ: fitData.scaleZ,
                yOffset: fitData.yOffset,
              });
            } else {
              const gb = garmentBoundsRef.current.get(boundsKey);
              console.log(
                "[FIT] null",
                String(item?.category),
                "hasMannequinBounds=",
                !!mannequinBounds,
                "manH=",
                mannequinBounds?.size?.y,
                "hasGarmentBounds=",
                !!gb,
                "gH=",
                gb?.size?.y,
                "boundsKey=",
                boundsKey
              );
            }

            let occluderItem = null;
            let occluderMin = null;
            let occluderMax = null;
            let occluderFitData = null;

            if (item === selectedItems.bottom) {
              if (overlapMode === "untucked") {
                occluderItem = selectedItems.top || null;
              } else if (overlapMode === "outer") {
                occluderItem = selectedItems.outer || null;
              }
            }

            if (occluderItem?.meshUrl) {
              const occluderKey = occluderItem.meshUrl;
              const ob = garmentBoundsRef.current.get(occluderKey);

              if (ob?.min && ob?.max) {
                occluderMin = ob.min.clone();
                occluderMax = ob.max.clone();
              }

              if (ob && mannequinBounds) {
                occluderFitData = computeGarmentFit({
                  category: occluderItem?.category,
                  variant: occluderItem?.variant,
                  garmentBounds: ob,
                  mannequinBounds,
                  garmentMetrics: garmentMetricsMap?.[garmentKeyFromUrl(occluderItem?.meshUrl || "")] || null,
                  bodyRefs,
                });

                if (occluderFitData) {
                  const occBaseCat = String(occluderItem?.category || "").split(":")[0];

                  if (occBaseCat === "outer") {
                    const boxH = ob?.size?.y || 1;

                    occluderFitData = {
                      ...occluderFitData,
                      scaleX: THREE.MathUtils.clamp(occluderFitData.scaleX, 0.85, 1.10),
                      scaleY: THREE.MathUtils.clamp(occluderFitData.scaleY, 0.85, 1.10),
                      scaleZ: THREE.MathUtils.clamp(occluderFitData.scaleZ ?? occluderFitData.scaleX, 0.94, 1.10),
                      yOffset: THREE.MathUtils.clamp(occluderFitData.yOffset, -0.18 * boxH, 0.06 * boxH),
                    };
                  }
                }
              }
            }
          const needsStencilMaskPass =
              (overlapMode === "untucked" && item === selectedItems.top) ||
              (overlapMode === "outer" && item === selectedItems.outer);

            return (
              <React.Fragment key={`frag-${item?.meshUrl}-${shapeSig}-b${boundsTick}`}>
                {needsStencilMaskPass && (
                  <GarmentMeshWithTexture
                    key={`stencil-mask-${item?.meshUrl}-${shapeSig}-b${boundsTick}`}
                    item={item}
                    avatarParams={avatarParams}
                    poseSpec={filteredPoseSpec}
                    fitData={fitData}
                    onBounds={() => {}}
                    overlapMode={null}
                    clipY={null}
                    clipTarget={null}
                    stencilMode="occluderMask"
                    disableClip={true}
                  />
                )}

                <GarmentMeshWithTexture
                  key={`mesh-${item?.meshUrl}-${shapeSig}-b${boundsTick}`}
                  item={item}
                  avatarParams={avatarParams}
                  poseSpec={filteredPoseSpec}
                  fitData={fitData}
                  onBounds={(b) => {
                    if (!b?.size) return;

                    const prev = garmentBoundsRef.current.get(boundsKey);

                    const changed =
                      !prev ||
                      Math.abs((prev.size?.y ?? 0) - (b.size?.y ?? 0)) > 1e-4 ||
                      Math.abs((prev.size?.x ?? 0) - (b.size?.x ?? 0)) > 1e-4;

                    garmentBoundsRef.current.set(boundsKey, b);

                    if (changed) setBoundsTick((t) => t + 1);
                  }}
                  overlapMode={overlapMode}
                  clipY={clipY}
                  clipTarget={
                    overlapMode === "tucked" && item === selectedItems.top
                      ? "top"
                      : overlapMode === "untucked" && item === selectedItems.bottom
                      ? "bottom"
                      : overlapMode === "outer" && item === selectedItems.bottom
                      ? "bottom"
                      : null
                  }
                  occluderFrontSrc={
                    overlapMode === "untucked" && item === selectedItems.bottom
                      ? (
                          selectedItems.top?.extractedUrl ||
                          selectedItems.top?.try_on?.assets?.front ||
                          selectedItems.top?.front ||
                          selectedItems.top?.image ||
                          null
                        )
                      : overlapMode === "outer" && item === selectedItems.bottom
                      ? (
                          selectedItems.outer?.extractedUrl ||
                          selectedItems.outer?.try_on?.assets?.front ||
                          selectedItems.outer?.front ||
                          selectedItems.outer?.image ||
                          null
                        )
                      : null
                  }
                  occluderBackSrc={
                    overlapMode === "untucked" && item === selectedItems.bottom
                      ? (
                          selectedItems.top?.extractedBackUrl ||
                          selectedItems.top?.try_on?.assets?.back ||
                          selectedItems.top?.back ||
                          selectedItems.top?.imageBack ||
                          selectedItems.top?.extractedUrl ||
                          selectedItems.top?.try_on?.assets?.front ||
                          selectedItems.top?.front ||
                          selectedItems.top?.image ||
                          null
                        )
                      : overlapMode === "outer" && item === selectedItems.bottom
                      ? (
                          selectedItems.outer?.extractedBackUrl ||
                          selectedItems.outer?.try_on?.assets?.back ||
                          selectedItems.outer?.back ||
                          selectedItems.outer?.imageBack ||
                          selectedItems.outer?.extractedUrl ||
                          selectedItems.outer?.try_on?.assets?.front ||
                          selectedItems.outer?.front ||
                          selectedItems.outer?.image ||
                          null
                        )
                      : null
                  }
                  occluderMin={occluderMin}
                  occluderMax={occluderMax}
                  occluderCategory={occluderItem?.category || null}
                  occluderFitData={occluderFitData}
                  stencilMode={
                    (overlapMode === "untucked" && item === selectedItems.bottom) ||
                    (overlapMode === "outer" && item === selectedItems.bottom)
                      ? "rejectOccluder"
                      : "none"
                  }
                  disableClip={
                    (overlapMode === "untucked" && item === selectedItems.bottom) ||
                    (overlapMode === "outer" && item === selectedItems.bottom)
                  }
                />
              </React.Fragment>
            );
          })}
        </group>
      </group>
    </>
  );
  }
useGLTF.preload(MANNEQUIN_GLB_URL);
